import React, { useState, useEffect, useContext } from "react";
import { api } from "../lib/api";

const AuthContext = React.createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const checkAuth = async () => {
    // Fast path: no token = no auth, skip network call entirely
    const token = localStorage.getItem('session_token');
    if (!token) {
      setUser(null);
      setSubscription(null);
      setLoading(false);
      return null;
    }

    try {
      // Fire both requests in parallel for speed
      const authPromise = api.get("/auth/me");
      const subPromise = api.get("/subscription/status").catch(() => null);

      const userRes = await authPromise;
      setUser(userRes.data);

      // Subscription result comes from parallel call
      const subRes = await subPromise;
      if (subRes?.data) setSubscription(subRes.data);

      // Link pre-login quiz result to user (one-time)
      const quizResult = localStorage.getItem("quiz_dosha_result");
      if (quizResult && !userRes.data?.dosha) {
        try {
          const parsed = JSON.parse(quizResult);
          if (parsed.dosha) {
            await api.post("/profile/link-quiz", { dosha: parsed.dosha, scores: parsed.scores });
            localStorage.removeItem("quiz_dosha_result");
          }
        } catch {}
      }

      return userRes.data;
    } catch {
      setUser(null);
      setSubscription(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const refreshSubscription = async () => {
    try {
      const subRes = await api.get("/subscription/status");
      setSubscription(subRes.data);
    } catch (e) {
      console.error("Subscription refresh error:", e);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    setSubscription(null);
    localStorage.removeItem('session_token');
    document.cookie = "session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = "session_token=; path=/; domain=" + window.location.hostname + "; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    sessionStorage.clear();
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, subscription, refreshSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};
